import { combineLatestWith, filter, map, tap } from 'rxjs';
import { ProfileComment } from '../../../../domain/entities/Comment';
import { toPromise } from '../../core/helpers/toPromise';
import { userOfferVotes } from '../../votes/endpoints';
import { getCommentsByNickname as getCommentsByNicknameEndpoint } from '../endpoints';
import { GetCommentsByNicknameResult } from '../types/GetCommentsByNickname';

export function getCommentsByNicknameSSR(nickname: string) {
  return getCommentsByNicknameEndpoint
    .requestAsPromise({ nickname })
    .then((result) => result.data)
    .catch(() => undefined);
}

export function populateCommentsByNicknameFromSSR(
  nickname: string,
  result: GetCommentsByNicknameResult
) {
  const offerIds = result?.edges?.map((edge) => edge.offer.id);
  getCommentsByNicknameEndpoint.cache$.next({
    currentNickname: nickname,
    loading: false,
    paginationLoading: false,
    pageInfo: result?.pageInfo,
    comments: result?.edges || [],
  });
  userOfferVotes.requestAsPromise({ offerIds });
}

export const getCommentsByNickname = (nickname: string, after?: string) =>
  toPromise(
    getCommentsByNicknameEndpoint
      .request({ nickname, commentsAfter: after })
      .pipe(
        filter(({ data }) => !!data),
        tap((message) => {
          const offerIds =
            message.data?.edges.map((comment) => comment.offer.id) || [];
          userOfferVotes.requestAsPromise({ offerIds });
        })
      )
  );

export const commentsCacheWithVote$ = getCommentsByNicknameEndpoint.cache$.pipe(
  combineLatestWith(userOfferVotes.cache$),
  map(([commentsCache, votes]) => {
    const nextTemperature = (offerId: string, current?: number) =>
      typeof votes?.[offerId]?.temperature === 'number'
        ? votes?.[offerId]?.temperature
        : current;

    const mappedDeals = commentsCache?.comments?.map(
      (comment: ProfileComment) => ({
        ...comment,
        offer: {
          ...comment.offer,
          temperature:
            nextTemperature(comment.offer.id, comment.offer.temperature) || 0,
          voteInfo: votes?.[comment.offer.id],
        },
      })
    );

    return {
      ...commentsCache,
      comments: mappedDeals,
    };
  })
);
